import { bookingModeOf, BookingSettings, isHttpUrl } from "./bookingLink";
import { businessTypeOf } from "./businessType";
import { ReservationType } from "./reservationTypes";
import { cancelRequestOn } from "./cancelRequest";
import { topicPosition } from "../IntentSection/keyTopicOrder";

/** The legacy menu records whose ``label``/``orderId``/``enabled`` override a capability's button. */
export type MenuRecordKey = "reservation" | "cancel_modify_booking" | "contact_us" | "talk_to_agent";
export const MENU_RECORD_KEYS: MenuRecordKey[] = ["reservation", "cancel_modify_booking", "contact_us", "talk_to_agent"];

/** The chatbot's positions for a button without an ``orderId`` override. */
export const DEFAULT_MENU_ORDER = { reservation: 1, reservation_type: 1, cancel_modify_booking: 2, contact_us: 3, talk_to_agent: 4 };

export interface MenuRecord {
  label?: string;
  order_id?: string | number;
  orderId?: string | number;
  enabled?: boolean | string;
  content?: Record<string, any>;
  group_property?: any[];
  property_type?: string;
  [key: string]: any;
}

export type MenuRecords = Record<MenuRecordKey, MenuRecord>;

/** The chatbot's label for a button the admin has not relabelled. */
export function defaultMenuLabel(key: MenuRecordKey, profile?: string): string {
  switch (key) {
    case "reservation":
      return profile === "general" ? "Book now" : "Book a room";
    case "cancel_modify_booking":
      return "Cancel booking";
    case "contact_us":
      return "Contact us";
    default:
      return "Talk to an agent";
  }
}

const text = (value: unknown) => String(value ?? "").trim();

export function isDisabled(record: MenuRecord | null | undefined): boolean {
  const enabled = record?.enabled;
  if (enabled === undefined || enabled === null) return false;
  if (typeof enabled === "string") return ["false", "0", "no", "off"].includes(enabled.trim().toLowerCase());
  return !enabled;
}

/** Explicit position override, or ``null`` when the record has none. */
export function orderOverride(record: MenuRecord | null | undefined): number | null {
  const value = record?.orderId ?? record?.order_id;
  if (value === undefined || value === null || text(value) === "") return null;
  const order = Number(value);
  return Number.isInteger(order) ? order : null;
}

export function menuOrder(record: MenuRecord | null | undefined, key: MenuRecordKey): number {
  return orderOverride(record) ?? DEFAULT_MENU_ORDER[key];
}

export function hasContactDetails(contact: MenuRecord | null | undefined): boolean {
  const content = contact?.content || {};
  return ["contactNo", "phone", "email", "address"].some((key) => text(content[key]));
}

/** Same fallback the chatbot uses: agent WhatsApp, agent number, then the contact number. */
export function agentNumber(agent: MenuRecord | null | undefined, contact: MenuRecord | null | undefined): string {
  return text(agent?.content?.whatsappNo) || text(agent?.content?.contactNo) || text(contact?.content?.contactNo);
}

/** Why the Cancel Booking button has no link, or ``null`` when it has one. */
export function missingCancelLink(settings: BookingSettings, cancel: MenuRecord | null | undefined): string | null {
  const mode = bookingModeOf(settings);
  if (mode === "none") return "Booking is turned off.";
  if (mode === "staah") return null;
  if ((settings.booking_engine || "swiftbook") === "swiftbook") {
    return text(settings.booking_engine_property_id) ? null : "Add the SwiftBook property ID.";
  }
  const url = text(cancel?.content?.url);
  return url && /^https?:\/\/\S+$/i.test(url) && isHttpUrl(url) ? null : "Add a cancel page link under Cancel booking.";
}

/**
 * A record as the editor holds it: blank label = chatbot default, ``enabled``
 * always set. An existing Cancel Booking record with a blank label was the
 * legacy way of hiding the button, so it loads as turned off.
 */
export function editableMenuRecord(key: MenuRecordKey, record: MenuRecord, exists: boolean): MenuRecord {
  const label = exists ? String(record.label ?? "") : "";
  const enabled = !isDisabled(record) && !(key === "cancel_modify_booking" && exists && !label.trim());
  return { ...record, label, enabled };
}

export interface MenuButton {
  key: string;
  record?: MenuRecordKey;
  name: string;
  label: string;
  /** The label as entered; blank means the default. */
  customLabel?: string;
  defaultLabel?: string;
  order: number;
  enabled: boolean;
  shown: boolean;
  hiddenReason?: string;
}

/**
 * The buttons the chatbot derives from these settings, in capability order:
 * the room booking button, one per reservation type, Cancel Booking, Contact
 * and Talk to an Agent. ``shown`` says whether the chatbot will display it.
 */
export function deriveMenuButtons(
  settings: BookingSettings & { assistant_profile?: string; business_type?: string },
  reservationTypes: ReservationType[],
  records: MenuRecords,
): MenuButton[] {
  const profile = businessTypeOf(settings) === "hotel" ? "hotel" : "general";
  const bookingOn = bookingModeOf(settings) !== "none";
  const buttons: MenuButton[] = [];
  const legacy = (key: MenuRecordKey, name: string, missing: string | null): MenuButton => {
    const record = records[key];
    const enabled = !isDisabled(record);
    const defaultLabel = defaultMenuLabel(key, profile);
    return {
      key,
      record: key,
      name,
      label: text(record?.label) || defaultLabel,
      customLabel: String(record?.label ?? ""),
      defaultLabel,
      order: menuOrder(record, key),
      enabled,
      shown: enabled && !missing,
      hiddenReason: missing || (enabled ? undefined : "Turned off."),
    };
  };

  if (bookingOn) buttons.push(legacy("reservation", "Room booking", null));
  reservationTypes.forEach((type, index) => {
    buttons.push({
      key: `reserve_${type.id || index}`,
      name: "Reservation type",
      label: type.label.trim() || "(no label)",
      order: DEFAULT_MENU_ORDER.reservation_type,
      enabled: true,
      shown: true,
    });
  });
  // With the cancel request flow on, the chatbot shows the button whatever the booking mode and asks for details instead.
  const cancelFlow = cancelRequestOn(records.cancel_modify_booking);
  if (bookingOn || cancelFlow) {
    const missing = cancelFlow ? null : missingCancelLink(settings, records.cancel_modify_booking);
    buttons.push(legacy("cancel_modify_booking", "Cancel booking", missing));
  }
  buttons.push(
    legacy("contact_us", "Contact", hasContactDetails(records.contact_us) ? null : "Add an address, email or phone number."),
  );
  buttons.push(
    legacy(
      "talk_to_agent",
      "Talk to an agent",
      agentNumber(records.talk_to_agent, records.contact_us) ? null : "Add a WhatsApp or contact number.",
    ),
  );
  return buttons;
}

/** Highest fixed button position (its default when not moved); key topics go after it. */
export function fixedMenuMaxOrder(records: Partial<Record<MenuRecordKey, MenuRecord | null | undefined>>): number {
  return Math.max(...MENU_RECORD_KEYS.map((key) => menuOrder(records[key], key)));
}

/** A parent FAQ topic record (``KeyTopics`` from the property config). */
export interface KeyTopicRecord {
  intentID?: string;
  label?: string;
  order_id?: string | number;
  orderId?: string | number;
  enabled?: boolean | string;
}

/** The menu buttons the chatbot adds for key topics: enabled and labelled ones. */
export function keyTopicButtons(topics: KeyTopicRecord[] | null | undefined): MenuButton[] {
  return (Array.isArray(topics) ? topics : [])
    .filter((topic) => !isDisabled(topic) && text(topic.label))
    .map((topic) => ({
      key: `topic_${topic.intentID}`,
      name: "Key topic",
      label: text(topic.label),
      order: topicPosition(topic),
      enabled: true,
      shown: true,
    }));
}

/**
 * The full widget menu in the chatbot's order: shown fixed buttons, then key
 * topics, sorted by position with a stable sort (as in the chatbot).
 */
export function menuPreview(buttons: MenuButton[], topics: MenuButton[] = []): MenuButton[] {
  return [...buttons.filter((button) => button.shown), ...topics]
    .map((button, index) => ({ button, index }))
    .sort((a, b) => a.button.order - b.button.order || a.index - b.index)
    .map(({ button }) => button);
}

function carriesData(key: MenuRecordKey, record: MenuRecord): boolean {
  switch (key) {
    case "reservation":
      return record.property_type === "G" || (record.group_property || []).length > 0;
    case "contact_us":
      return hasContactDetails(record) || Boolean(text(record.content?.whatsappNo));
    case "talk_to_agent":
      return Boolean(agentNumber(record, null));
    default:
      return Boolean(text(record.content?.url)) || Boolean(record.content?.cancelRequest);
  }
}

function hasOverride(record: MenuRecord): boolean {
  return Boolean(text(record.label)) || isDisabled(record) || (record.orderId !== undefined && text(record.orderId) !== "");
}

/**
 * Legacy records for ``/mandatory_info``. A record is written only when it
 * already exists, overrides its button, or carries data the chatbot reads
 * (contact details, the agent's number, group properties, a cancel URL or
 * cancel request settings), so a
 * fresh property gets none of them just for saving its settings.
 */
export function menuRecordsToSave(records: MenuRecords, exists: Record<MenuRecordKey, boolean>): MenuRecord[] {
  return MENU_RECORD_KEYS.flatMap((key) => {
    const record = records[key];
    if (!exists[key] && !hasOverride(record) && !carriesData(key, record)) return [];
    const enabled = !isDisabled(record);
    const label = text(record.label);
    const order = record.orderId !== undefined && text(record.orderId) !== "" ? Number(record.orderId) : null;
    const savedLabel =
      key === "reservation" ? label : key === "cancel_modify_booking" && !enabled ? label : label || defaultMenuLabel(key);
    return [{
      ...record,
      label: savedLabel,
      enabled,
      ...(order !== null && Number.isInteger(order) ? { orderId: order, order_id: String(order) } : {}),
      isModify: "Y",
    }];
  });
}
